import { HttpClient } from '@angular/common/http';
import { USER_SETTINGS_SERVICE } from '@infinitform/platform-core';
import { inject, Injectable } from '@angular/core';
import { EnhancedUiService } from '@infinitform/shared-ui';
import { lastValueFrom } from 'rxjs';
import { Engine } from '../engine/engine';
import { DEFAULT_ENGINE_GRAPHICS, EngineGraphicsOptions } from '../engine/graphics-options';
import { environment } from '../config/editor-environment';
import { KeyboardShortcutsService } from './keyboard-shortcuts.service';

/** Minimal shape of any TrackballControls-like object that can receive a user mouse preset. */
interface TrackballLike {
  mouseButtons: {
    LEFT?: number;
    MIDDLE?: number;
    RIGHT?: number;
    SHIFTMIDDLE?: number;
    SHIFTRIGHT?: number;
    CTRLMIDDLE?: number;
    DOUBLEMIDDLE?: number;
  };
  invertWheelZoom?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SettingsAndPresetsService {
  private readonly userSettingsService = inject(USER_SETTINGS_SERVICE, { optional: true });
  private http = inject(HttpClient);
  public keyboardShortcuts = inject(KeyboardShortcutsService);
  private readonly enhancedUi = inject(EnhancedUiService);

  // Mouse Settings
  public currentMousePreset: any;
  // Graphics Settings
  public currentGraphicsSettings: any;

  // Engine reference - set externally by SessionService
  private engine?: Engine;

  /**
   * Secondary TrackballControls instances (e.g. per-tile controls in the
   * multi-MF compare view) that want to receive the same mouse preset as
   * the main engine controls. Populated via `registerExternalControls`.
   */
  private externalControls = new Set<TrackballLike>();

  setEngine(engine: Engine): void {
    this.engine = engine;
  }

  // Methods not used in cloud version
  public initGraphicsPreset(): void {
    this.getCurrentGraphicsPreset();
  }

  public getCurrentGraphicsPreset(): void {}

  public setCurrentGraphicsSettings(): void {
    setTimeout(async () => await this.setUserSettings());
  }

  public async changeGraphicsSettings(): Promise<void> {
    this.engine?.windowResize();
    this.setCurrentGraphicsSettings();
  }

  /**
   * Builds the engine's lighting options from the persisted graphics
   * settings, filling in `DEFAULT_ENGINE_GRAPHICS` when absent -- a document
   * saved before lighting existed.
   */
  private toEngineGraphicsOptions(): EngineGraphicsOptions {
    const graphics = this.currentGraphicsSettings ?? {};
    return {
      lighting: graphics.lighting ?? DEFAULT_ENGINE_GRAPHICS.lighting
    };
  }

  /** Pushes the current lighting settings into the engine. Safe to call before or after `engine.init()`. */
  public applyEngineGraphicsOptions(): void {
    this.engine?.applyGraphicsOptions(this.toEngineGraphicsOptions());
  }

  /** For a user-made change to lighting: apply it immediately and persist it. */
  public changeEngineGraphicsOptions(): void {
    this.applyEngineGraphicsOptions();
    this.setCurrentGraphicsSettings();
  }

  // Mouse
  private lastMiddleClickTime = 0;
  private DOUBLE_CLICK_INTERVAL = 300;
  private auxclickHandler: ((event: MouseEvent) => void) | null = null;

  public initMousePreset(): void {
    // Gets the current preset or applies default one
    this.getCurrentMousePreset();

    // Remove previous listener if any to prevent stacking
    if (this.auxclickHandler && this.engine?.canvas) {
      this.engine.canvas.removeEventListener('auxclick', this.auxclickHandler);
    }

    this.auxclickHandler = (event: MouseEvent) => {
      if (event.button === 1) {
        // 1 = middle mouse button
        const currentTime = Date.now();
        if (currentTime - this.lastMiddleClickTime < this.DOUBLE_CLICK_INTERVAL) {
          if (this.currentMousePreset['DOUBLEMIDDLE'] === 1) {
            this.engine?.viewHelper?.fitCameraToObject(this.engine?.group!, 1);
          }
          this.lastMiddleClickTime = 0;
        } else {
          // Store timestamp of first click
          this.lastMiddleClickTime = currentTime;
        }
      }
    };

    this.engine?.canvas.addEventListener('auxclick', this.auxclickHandler);
  }

  /** Apply the loaded preset. A read must not write it straight back. */
  public getCurrentMousePreset(): void {
    this.applyMousePreset();
  }

  /** Apply the preset AND persist it — for a user-made change. */
  public setCurrentMousePreset(): void {
    this.applyMousePreset();
    setTimeout(async () => await this.setUserSettings());
  }

  private applyMousePreset(): void {
    if (this.engine?.controls) {
      this.applyMousePresetToControls(this.engine.controls);
    }
    for (const controls of this.externalControls) {
      this.applyMousePresetToControls(controls);
    }
  }

  /**
   * Apply the current mouse preset to any TrackballControls-like object.
   * Missing / negative preset slots map to -1 (disabled), matching the
   * existing behavior for the main engine controls.
   */
  public applyMousePresetToControls(controls: TrackballLike): void {
    const preset = this.currentMousePreset;
    if (!preset || !controls?.mouseButtons) return;
    controls.mouseButtons.LEFT = preset.LEFT >= 0 ? preset.LEFT : -1;
    controls.mouseButtons.MIDDLE = preset.MIDDLE >= 0 ? preset.MIDDLE : -1;
    controls.mouseButtons.RIGHT = preset.RIGHT >= 0 ? preset.RIGHT : -1;
    controls.mouseButtons.SHIFTMIDDLE = preset.SHIFTMIDDLE >= 0 ? preset.SHIFTMIDDLE : -1;
    controls.mouseButtons.SHIFTRIGHT = preset.SHIFTRIGHT >= 0 ? preset.SHIFTRIGHT : -1;
    controls.mouseButtons.CTRLMIDDLE = preset.CTRLMIDDLE >= 0 ? preset.CTRLMIDDLE : -1;
    controls.mouseButtons.DOUBLEMIDDLE = preset.DOUBLEMIDDLE >= 0 ? preset.DOUBLEMIDDLE : -1;
    // Absent on settings saved before the option existed: the default direction.
    controls.invertWheelZoom = preset.invertWheelZoom === true;
  }

  /**
   * Register a secondary TrackballControls instance (e.g. a compare-view
   * tile's own controls) so it receives the current preset immediately and
   * any future preset changes. Callers MUST pair this with
   * `unregisterExternalControls` on teardown.
   */
  public registerExternalControls(controls: TrackballLike): void {
    this.externalControls.add(controls);
    this.applyMousePresetToControls(controls);
  }

  public unregisterExternalControls(controls: TrackballLike): void {
    this.externalControls.delete(controls);
  }

  /**
   * Attach the DOUBLEMIDDLE "fit camera" auxclick handler to an arbitrary
   * canvas (e.g. a compare-view tile). Returns a teardown function the
   * caller invokes on destroy. The handler only fires when the user's
   * preset has `DOUBLEMIDDLE === 1`, matching the main engine behavior.
   */
  public attachDoubleMiddleFitHandler(
    canvas: HTMLCanvasElement,
    onFit: () => void,
  ): () => void {
    let lastTime = 0;
    const handler = (event: MouseEvent): void => {
      if (event.button !== 1) return;
      const now = Date.now();
      if (now - lastTime < this.DOUBLE_CLICK_INTERVAL) {
        if (this.currentMousePreset?.DOUBLEMIDDLE === 1) onFit();
        lastTime = 0;
      } else {
        lastTime = now;
      }
    };
    canvas.addEventListener('auxclick', handler);
    return () => canvas.removeEventListener('auxclick', handler);
  }

  public changeMousePresetSingle(button: any, action: any): void {
    this.currentMousePreset[button] = action;
    this.setCurrentMousePreset();
  }

  // Local Storage
  public getSettingBoolean(key: string): boolean | null {
    const value = localStorage.getItem(key);
    if (value) return value === 'yes';
    return null;
  }

  public setSetting(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  /**
   * The graphics object to save, carrying the Enhanced UI value this window
   * shows. The backend replaces `graphics` whole, so a save without it would
   * drop the setting. Nothing is sent before the settings have loaded.
   */
  private graphicsForSave(): Record<string, unknown> | undefined {
    if (!this.currentGraphicsSettings) return undefined;
    return { ...this.currentGraphicsSettings, enhancedUi: this.enhancedUi.enabled() };
  }

  // User Settings
  async getUserSettings(): Promise<any> {
    // Shared with the other consumers of this endpoint via the platform token.
    if (!this.userSettingsService) return null;
    return lastValueFrom(this.userSettingsService.get());
  }

  async setUserSettings(): Promise<any> {
    if (!this.userSettingsService) return null;
    return lastValueFrom(
      this.userSettingsService.update({
        graphics: this.graphicsForSave(),
        mouse: this.currentMousePreset,
        keyboardShortcuts: this.keyboardShortcuts.exportForSync()
      } as never)
    );
  }
}
